import React, { useEffect, useMemo, useRef, useState } from 'react'
import DailyIframe from '@daily-co/daily-js'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, query, getDocs, getDoc, where, addDoc, updateDoc, serverTimestamp, doc } from 'firebase/firestore'
import { getDatabase, ref, onValue, push, set, serverTimestamp as realtimeTimestamp } from 'firebase/database'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  faCameraRetro,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
  faDisplay,
  faComments,
  faExpand,
  faCompress,
  faEllipsis,
  faPhoneSlash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { db } from '../firebase'
import sampleImage from '../Images/defprofile.png'

const WORKSPACE_CALLS_API_URL = '/workspaceCallsApi'

function VideoRoom() {
  const auth = getAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const myData = location.state?.myData
  const workspaceCallId = location.state?.workspaceCallId || ''
  const workspaceCallRoomName = location.state?.workspaceCallRoomName || ''
  const workspaceCallRoomUrl = location.state?.workspaceCallRoomUrl || ''
  const workspaceCallJoinUrl = location.state?.workspaceCallJoinUrl || ''
  const realtimeDb = getDatabase()
  const startedBy = location.state?.startedBy || ''
  const startedByName = location.state?.startedByName || ''
  const channelName = location.state?.channelName || ''
  const startMuted = Boolean(location.state?.startMuted)
  const startVideoOff = Boolean(location.state?.startVideoOff)
  const audioDeviceId = location.state?.audioDeviceId || ''
  const videoDeviceId = location.state?.videoDeviceId || ''
  const attendanceSource = location.state?.attendanceSource || ''
  const shouldLogAttendance = attendanceSource === 'my-rooms'

  const [loggedInUser, setLoggedInUser] = useState('')
  const [roomError, setRoomError] = useState('')
  const [roomUrl, setRoomUrl] = useState('')
  const [participants, setParticipants] = useState([])
  const [isMuted, setIsMuted] = useState(startMuted)
  const [isVideoOff, setIsVideoOff] = useState(startVideoOff)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [activePanel, setActivePanel] = useState('participants')
  const [callStartMs, setCallStartMs] = useState(null)
  const [clockMs, setClockMs] = useState(Date.now())
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [userDirectory, setUserDirectory] = useState(new Map())
  const [isStageFullscreen, setIsStageFullscreen] = useState(false)
  const [channelMemberTotal, setChannelMemberTotal] = useState(null)

  const callObjectRef = useRef(null)
  const hasJoinedRef = useRef(false)
  const stageRef = useRef(null)
  const stageVideoRef = useRef(null)
  const audioRefs = useRef({})
  const joinTimeRef = useRef(0)
  const participantCountRef = useRef(0)

  const DAILY_API_KEY =
    process.env.REACT_APP_DAILY_API_KEY ||
    '#'
  const DAILY_DOMAIN = process.env.REACT_APP_DAILY_DOMAIN || 'https://ubunifucollege.daily.co'
  const DAILY_API_URL = process.env.REACT_APP_DAILY_API_URL || 'https://api.daily.co/v1'

  const sanitizeRoomName = (value) => {
    if (!value) return ''
    return value.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 80)
  }

  const roomName = useMemo(() => sanitizeRoomName(workspaceCallRoomName || myData || `session-${Date.now()}`), [workspaceCallRoomName, myData])
  const displayChannelName = channelName || 'Channel'
  const isModerator = Boolean(startedBy && auth.currentUser?.uid === startedBy)
  const channelId = useMemo(() => {
    if (!myData) return ''
    return myData.startsWith('channel-') ? myData.replace('channel-', '') : ''
  }, [myData])
  const dmIds = useMemo(() => {
    if (!myData || !myData.startsWith('dm-')) return []
    return myData.replace('dm-', '').split('-').filter(Boolean)
  }, [myData])
  const dmChatId = dmIds.length === 2 ? `${dmIds[0]}_${dmIds[1]}` : ''
  const isChannelCall = Boolean(channelId)

  useEffect(() => {
    let isMounted = true
    const fetchUsers = async () => {
      try {
        const usersSnap = await getDocs(query(collection(db, 'users'), where('Status', '==', 'Active')))
        const map = new Map()
        usersSnap.forEach((docSnap) => {
          const data = docSnap.data()
          if (data?.userId) {
            map.set(data.userId, {
              id: data.userId,
              name: data.bio || data.userId,
              avatar: data.profilePic || sampleImage,
            })
          }
        })
        if (isMounted) setUserDirectory(map)
      } catch (error) {
        console.error('Failed to load user directory:', error)
      }
    }
    fetchUsers()
    return () => {
      isMounted = false
    }
  }, [])

  const getUserNameById = (id) => {
    if (!id) return 'Student'
    const entry = userDirectory.get(id)
    return entry?.name || 'Student'
  }

  const getUserAvatarById = (id) => {
    if (!id) return sampleImage
    const entry = userDirectory.get(id)
    return entry?.avatar || sampleImage
  }

  const findAvatarForParticipant = (participant) => {
    if (!participant) return sampleImage
    for (const entry of userDirectory.values()) {
      if (entry.name === participant.name) return entry.avatar || sampleImage
    }
    return sampleImage
  }

  useEffect(() => {
    let isMounted = true
    const fetchChannelTotal = async () => {
      if (!channelId) {
        if (isMounted) setChannelMemberTotal(2)
        return
      }
      try {
        const channelSnap = await getDoc(doc(db, 'Teams', channelId))
        if (!channelSnap.exists()) {
          if (isMounted) setChannelMemberTotal(null)
          return
        }
        const data = channelSnap.data()
        const members = Array.isArray(data.members) ? data.members : []
        let total = members.length
        const name = (data.teamName || '').trim().toLowerCase()
        const isDefault = name === 'genral' || name === 'general'
        if (isDefault || total === 0) {
          const usersSnap = await getDocs(query(collection(db, 'users'), where('Status', '==', 'Active')))
          total = usersSnap.size
        }
        if (isMounted) setChannelMemberTotal(total)
      } catch (error) {
        console.error('Failed to load channel members:', error)
        if (isMounted) setChannelMemberTotal(null)
      }
    }

    fetchChannelTotal()
    return () => {
      isMounted = false
    }
  }, [channelId])

  const formatElapsed = (ms) => {
    if (!ms || ms < 0) return '00:00:00'
    const totalSeconds = Math.floor(ms / 1000)
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
    const seconds = String(totalSeconds % 60).padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  }

  const getTimestamp = (value) => {
    if (!value) return 0
    if (typeof value === 'number') return value
    if (value?.toMillis) return value.toMillis()
    if (value?.seconds) return value.seconds * 1000
    return 0
  }

  const formatTime = (value) => {
    const ts = getTimestamp(value)
    if (!ts) return ''
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
    })
  }

  const sanitizeMessageHtml = (input) => {
    if (!input) return ''
    const parser = new DOMParser()
    const doc = parser.parseFromString(input, 'text/html')
    const allowedTags = new Set([
      'b',
      'strong',
      'i',
      'em',
      'u',
      's',
      'code',
      'pre',
      'br',
      'p',
      'div',
      'ul',
      'ol',
      'li',
      'blockquote',
      'span',
    ])

    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName.toLowerCase()
          if (!allowedTags.has(tag)) {
            const fragment = doc.createDocumentFragment()
            while (child.firstChild) {
              fragment.appendChild(child.firstChild)
            }
            child.replaceWith(fragment)
            return
          }
          Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
          walk(child)
        } else if (child.nodeType === Node.COMMENT_NODE) {
          child.remove()
        }
      })
    }

    walk(doc.body)
    return doc.body.innerHTML
  }

  const getPlainTextFromHtml = (input) => {
    if (!input) return ''
    const parser = new DOMParser()
    const doc = parser.parseFromString(input, 'text/html')
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ')
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const uid = user.uid
        const q = query(collection(db, 'users'), where('userId', '==', uid))
        try {
          const snapshot = await getDocs(q)
          snapshot.forEach((docSnap) => {
            setLoggedInUser(docSnap.data().bio || user.displayName || user.email || 'Student')
          })
        } catch (error) {
          console.error('Error fetching user data: ', error)
          setLoggedInUser(user.displayName || user.email || 'Student')
        }
      } else {
        navigate('/login')
      }
    })

    return () => unsubscribe()
  }, [auth, navigate])

  const fetchRoom = async () => {
    if (!DAILY_API_KEY) {
      throw new Error('Missing Daily API key')
    }
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${DAILY_API_KEY}`,
    }

    const getRes = await fetch(`${DAILY_API_URL}/rooms/${roomName}`, { headers })
    if (getRes.ok) {
      return getRes.json()
    }
    if (getRes.status !== 404) {
      throw new Error('Failed to check room')
    }

    const createRes = await fetch(`${DAILY_API_URL}/rooms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: roomName,
        properties: {
          enable_chat: true,
          enable_screenshare: true,
          exp: Math.floor(Date.now() / 1000) + 60 * 60 * 6,
        },
      }),
    })

    if (!createRes.ok) {
      throw new Error('Failed to create room')
    }
    return createRes.json()
  }

  const deleteRoom = async () => {
    if (!DAILY_API_KEY || !roomName) return
    try {
      await fetch(`${DAILY_API_URL}/rooms/${roomName}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${DAILY_API_KEY}`,
        },
      })
    } catch (error) {
      console.error('Error deleting room: ', error)
    }
  }

  const updateWorkspaceCallSession = async (action) => {
    if (!workspaceCallId || !auth.currentUser) {
      return
    }

    try {
      const token = await auth.currentUser.getIdToken(true)
      const response = await fetch(WORKSPACE_CALLS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          callId: workspaceCallId,
        }),
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload?.error || 'We could not update this call right now.')
      }
    } catch (error) {
      try {
        const sessionRef = doc(db, 'workspaceCallSessions', workspaceCallId)
        const nextUpdate = {
          updatedAtMs: Date.now(),
        }

        if (action === 'end') {
          nextUpdate.status = 'ended'
          nextUpdate.endedBy = auth.currentUser.uid
          nextUpdate.endedAtMs = Date.now()
        } else if (action === 'accept') {
          nextUpdate.status = 'active'
          nextUpdate.acceptedBy = auth.currentUser.uid
          nextUpdate.startedAtMs = Date.now()
        } else if (action === 'decline') {
          nextUpdate.status = 'declined'
          nextUpdate.endedBy = auth.currentUser.uid
          nextUpdate.endedAtMs = Date.now()
        }

        await updateDoc(sessionRef, nextUpdate)
      } catch (fallbackError) {
        console.error('Workspace call update failed:', fallbackError || error)
      }
    }
  }

  const syncParticipants = () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    const list = Object.values(callObject.participants())
      .filter((item) => item.session_id)
      .map((item) => ({
        id: item.session_id,
        name: item.user_name || item.session_name || 'Student',
        isLocal: item.local,
        audioState: item.tracks?.audio?.state || 'off',
        videoState: item.tracks?.video?.state || 'off',
        screenState: item.tracks?.screenVideo?.state || 'off',
        audioTrack: item.tracks?.audio?.track || null,
        videoTrack: item.tracks?.video?.track || null,
        screenTrack: item.tracks?.screenVideo?.track || null,
      }))
    setParticipants(list)
    participantCountRef.current = list.length
    const local = list.find((item) => item.isLocal)
    if (local) {
      const withinGrace = joinTimeRef.current
        ? Date.now() - joinTimeRef.current < 1500
        : false
      if (!withinGrace) {
        setIsMuted(local.audioState !== 'playable')
        setIsVideoOff(local.videoState !== 'playable')
      } else {
        if (startMuted) setIsMuted(true)
        if (startVideoOff) setIsVideoOff(true)
      }
    }
  }

  useEffect(() => {
    let sessionDocId = null
    if (!loggedInUser || !roomName || hasJoinedRef.current) return
    hasJoinedRef.current = true

    const joinCall = async () => {
      try {
        const useWorkspaceJoinUrl = Boolean(workspaceCallJoinUrl)
        const room = useWorkspaceJoinUrl ? null : await fetchRoom()
        const url = workspaceCallJoinUrl || room?.url || `${DAILY_DOMAIN}/${roomName}`
        setRoomUrl(workspaceCallRoomUrl || url)

        const callObject = DailyIframe.createCallObject()
        callObjectRef.current = callObject
        callObject.setUserName(loggedInUser)
        if (audioDeviceId || videoDeviceId) {
          callObject.setInputDevices({
            audioDeviceId: audioDeviceId || undefined,
            videoDeviceId: videoDeviceId || undefined,
          })
        }

        callObject.on('participant-joined', syncParticipants)
        callObject.on('participant-updated', syncParticipants)
        callObject.on('participant-left', syncParticipants)
        callObject.on('track-started', syncParticipants)
        callObject.on('track-stopped', syncParticipants)
        callObject.on('local-screen-share-started', () => setIsScreenSharing(true))
        callObject.on('local-screen-share-stopped', () => setIsScreenSharing(false))
        callObject.on('joined-meeting', (event) => {
          const startTime = event?.meetingSession?.startTime
          const startMs = startTime ? new Date(startTime).getTime() : Date.now()
          setCallStartMs(startMs)
          joinTimeRef.current = Date.now()
          applyStartDeviceState()
          syncParticipants()
        })

        if (startMuted) {
          callObject.setLocalAudio(false)
        }
        if (startVideoOff) {
          callObject.setLocalVideo(false)
        }

        const joinOptions = {
          url,
          startAudioOff: startMuted,
          startVideoOff: startVideoOff,
        }

        callObject.join(joinOptions).then(async () => {
          try {
            if (shouldLogAttendance) {
              const sessionRef = await addDoc(collection(db, 'AttendaceReport'), {
                userId: auth.currentUser.uid,
                username: loggedInUser,
                startTime: serverTimestamp(),
                roomlink: url,
              })
              sessionDocId = sessionRef.id
            }
            setTimeout(() => {
              applyStartDeviceState()
              syncParticipants()
            }, 300)
          } catch (error) {
            console.error('Error creating session document: ', error)
          }
        }).catch(async () => {
          // Fallback for Daily environments that reject startAudioOff/startVideoOff
          try {
            await callObject.join({ url })
          } catch (error) {
            console.error('Call join failed: ', error)
          }
        })

        callObject.on('left-meeting', async () => {
          if (workspaceCallId) {
            await updateWorkspaceCallSession('end')
          }
          if (sessionDocId && shouldLogAttendance) {
            const sessionDocRef = doc(db, 'AttendaceReport', sessionDocId)
            updateDoc(sessionDocRef, {
              endTime: serverTimestamp(),
            }).catch((error) => {
              console.error('Error updating session document: ', error)
            })
          }
          if (participantCountRef.current <= 1) {
            await deleteRoom()
          }
          navigate(workspaceCallId ? '/workspace' : '/dashboard')
        })
      } catch (error) {
        console.error('Daily room error:', error)
        setRoomError('Unable to start the call. Please try again.')
      }
    }

    joinCall()

    return () => {
      if (callObjectRef.current) {
        callObjectRef.current.destroy()
        callObjectRef.current = null
      }
    }
  }, [
    loggedInUser,
    roomName,
    auth,
    DAILY_DOMAIN,
    navigate,
    audioDeviceId,
    videoDeviceId,
    startMuted,
    startVideoOff,
    shouldLogAttendance,
    workspaceCallId,
    workspaceCallJoinUrl,
    workspaceCallRoomUrl,
  ])

  const toggleAudio = () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    const local = callObject.participants()?.local
    const isEnabled = local?.tracks?.audio?.state === 'playable'
    callObject.setLocalAudio(!isEnabled)
    setIsMuted(isEnabled)
  }

  const toggleVideo = () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    const local = callObject.participants()?.local
    const isEnabled = local?.tracks?.video?.state === 'playable'
    callObject.setLocalVideo(!isEnabled)
    setIsVideoOff(isEnabled)
  }

  const leaveCall = () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    callObject.leave()
  }

  const applyStartDeviceState = () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    if (startMuted) {
      callObject.setLocalAudio(false)
      setIsMuted(true)
    }
    if (startVideoOff) {
      callObject.setLocalVideo(false)
      setIsVideoOff(true)
    }
  }

  const toggleStageFullscreen = async () => {
    if (!stageRef.current) return
    try {
      if (document.fullscreenElement === stageRef.current) {
        await document.exitFullscreen()
      } else {
        await stageRef.current.requestFullscreen()
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  const toggleScreenShare = async () => {
    const callObject = callObjectRef.current
    if (!callObject) return
    try {
      if (isScreenSharing) {
        await callObject.stopScreenShare()
        setIsScreenSharing(false)
      } else {
        await callObject.startScreenShare()
        setIsScreenSharing(true)
      }
    } catch (error) {
      console.error('Screen share error:', error)
    }
  }

  const displayedParticipants = participants.slice(0, 4)
  const remainingCount = Math.max(0, participants.length - displayedParticipants.length)
  const joinedCount = participants.length
  const totalCount = channelMemberTotal ?? joinedCount

  const activeScreenShare = participants.find((member) => member.screenState === 'playable')
  const localVideoParticipant = participants.find(
    (member) => member.isLocal && member.videoState === 'playable'
  )
  const activeVideoParticipant = participants.find((member) => {
    if (member.videoState !== 'playable') return false
    if (startVideoOff && member.isLocal) return false
    return true
  })
  const activeParticipant = activeScreenShare
    || localVideoParticipant
    || activeVideoParticipant
    || (!startVideoOff ? participants[0] : null)
  const chatEnabled = isChannelCall || Boolean(dmChatId)

  useEffect(() => {
    if (!stageVideoRef.current) return
    if (!activeParticipant) {
      stageVideoRef.current.srcObject = null
      return
    }
    const tracks = []
    if (activeParticipant.screenTrack) {
      tracks.push(activeParticipant.screenTrack)
    } else if (activeParticipant.videoTrack) {
      tracks.push(activeParticipant.videoTrack)
    }
    if (activeParticipant.audioTrack && !activeParticipant.isLocal) tracks.push(activeParticipant.audioTrack)
    const stream = new MediaStream(tracks)
    if (stageVideoRef.current.srcObject !== stream) {
      stageVideoRef.current.srcObject = stream
    }
    stageVideoRef.current.muted = activeParticipant.isLocal
    stageVideoRef.current.play().catch(() => {})
  }, [activeParticipant])

  useEffect(() => {
    participants.forEach((member) => {
      if (member.isLocal) return
      const el = audioRefs.current[member.id]
      if (!el) return
      if (!member.audioTrack) {
        el.srcObject = null
        return
      }
      const stream = new MediaStream([member.audioTrack])
      if (el.srcObject !== stream) {
        el.srcObject = stream
      }
      el.play().catch(() => {})
    })
  }, [participants])

  useEffect(() => {
    if (!callStartMs) return
    const interval = setInterval(() => {
      setClockMs(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [callStartMs])

  useEffect(() => {
    const handler = () => {
      setIsStageFullscreen(document.fullscreenElement === stageRef.current)
    }
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const elapsedMs = callStartMs ? Math.max(0, clockMs - callStartMs) : 0

  useEffect(() => {
    const chatPath = isChannelCall ? `channelChats/${channelId}` : dmChatId ? `chats/${dmChatId}` : ''
    if (!chatPath) {
      setChatMessages([])
      return undefined
    }
    const chatRef = ref(realtimeDb, chatPath)
    const unsubscribe = onValue(chatRef, (snapshot) => {
      if (!snapshot.exists()) {
        setChatMessages([])
        return
      }
      const items = Object.entries(snapshot.val()).map(([id, value]) => ({
        id,
        ...value,
        senderName: value.senderName || getUserNameById(value.senderId),
      }))
      const filtered = items.filter((msg) => msg.fromVideoCall === true)
      filtered.sort((a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp))
      setChatMessages(filtered)
    })
    return () => unsubscribe()
  }, [realtimeDb, channelId, dmChatId, isChannelCall, userDirectory])

  const sendChatMessage = () => {
    if (!isChannelCall && !dmChatId) return
    const cleanHtml = sanitizeMessageHtml(chatInput)
    const plainText = getPlainTextFromHtml(cleanHtml).trim()
    if (!plainText) return
    const senderId = auth.currentUser?.uid || ''
    if (isChannelCall) {
      const messageData = {
        text: cleanHtml,
        senderId,
        senderName: loggedInUser || 'Student',
        fromVideoCall: true,
        timestamp: realtimeTimestamp(),
        clientTimestamp: Date.now(),
      }
      push(ref(realtimeDb, `channelChats/${channelId}`), messageData)
      setChatInput('')
      return
    }

    if (dmIds.length !== 2) return
    const receiverId = dmIds.find((id) => id !== senderId) || ''
    const chatRef = ref(realtimeDb, `chats/${dmChatId}`)
    const newMsgRef = push(chatRef)
    const messageData = {
      text: cleanHtml,
      senderId,
      receiverId,
      fromVideoCall: true,
      timestamp: realtimeTimestamp(),
      clientTimestamp: Date.now(),
      isRead: false,
    }
    if (newMsgRef.key) {
      const fullMessage = { ...messageData, messageId: newMsgRef.key }
      const mirrorId = newMsgRef.key
      const mirrorChatId = `${dmIds[1]}_${dmIds[0]}`
      const mirrorRef = ref(realtimeDb, `chats/${mirrorChatId}/${mirrorId}`)
      set(newMsgRef, fullMessage)
      set(mirrorRef, fullMessage)
    }
    setChatInput('')
  }

  return (
    <div className="videoRoomShell">
      <div className="videoRoomCard">
        <div className="videoRoomAudioLayer">
          {participants
            .filter((member) => !member.isLocal)
            .map((member) => (
              <audio
                key={member.id}
                ref={(el) => {
                  if (el) {
                    audioRefs.current[member.id] = el
                  } else {
                    delete audioRefs.current[member.id]
                  }
                }}
                autoPlay
                playsInline
              />
            ))}
        </div>
        <header className="videoRoomHeader">
          <div className="videoRoomBrand">
            <div className="videoRoomLogo">
              <FontAwesomeIcon icon={faCameraRetro} />
            </div>
            <div>
              <h4>Workspace Live Session</h4>
              <div className="videoRoomAttendance">
                {joinedCount} joined / {totalCount} members
              </div>
            </div>
          </div>
          <div className="videoRoomHeaderMiddle">
            <div className="videoRoomAvatars">
              {displayedParticipants.map((member) => (
                <div key={member.id} className="videoRoomAvatar">
                  {member.name.charAt(0).toUpperCase()}
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="videoRoomAvatar videoRoomAvatarMore">+{remainingCount}</div>
              )}
            </div>
            <div className="videoRoomLink">
              <span>{displayChannelName}</span>
            </div>
          </div>
          <div className="videoRoomProfile">
            <img src={auth.currentUser?.photoURL || sampleImage} alt="" />
            <div>
              <p>{loggedInUser || 'Student'}</p>
              <span>{isModerator ? 'Moderator' : startedByName ? `Host: ${startedByName}` : 'Participant'}</span>
            </div>
          </div>
        </header>

        <main className="videoRoomBody">
          <section className="videoRoomStage" ref={stageRef}>
            {roomError && <div className="videoRoomError">{roomError}</div>}
            {!roomError && !roomUrl && <div className="videoRoomLoading">Loading call...</div>}
            <div className="videoRoomStageInner">
              {activeParticipant ? (
                <video
                  className="videoRoomStageVideo"
                  ref={stageVideoRef}
                  autoPlay
                  playsInline
                />
              ) : (
                <div className="videoRoomPlaceholder">Camera is off</div>
              )}
            </div>
            <div className="videoRoomTimer">{formatElapsed(elapsedMs)}</div>
            <div className="videoRoomSpeaker">{loggedInUser || 'Student'}</div>
            {activeScreenShare && <div className="videoRoomShareBadge">Screen sharing</div>}
          </section>

          <aside className="videoRoomSidebar">
            <div className="videoRoomTabs">
              <button
                type="button"
                className={activePanel === 'participants' ? 'active' : ''}
                onClick={() => setActivePanel('participants')}
              >
                Participants
              </button>
              <button
                type="button"
                className={activePanel === 'chat' ? 'active' : ''}
                onClick={() => setActivePanel('chat')}
              >
                Chat
              </button>
            </div>

            {activePanel === 'participants' && (
              <div className="videoRoomPanel">
                <div className="videoRoomPanelHeader">
                  <h6>Participants</h6>
                </div>
                <div className="videoRoomParticipants">
                  {participants.length === 0 && (
                    <div className="videoRoomEmpty">Waiting for others to join...</div>
                  )}
                  {participants.map((member) => {
                    const avatarSrc = findAvatarForParticipant(member)
                    return (
                      <div key={member.id} className="videoRoomParticipant">
                        <div className="videoRoomParticipantAvatar">
                          <img src={avatarSrc} alt={member.name} />
                        </div>
                        <div className="videoRoomParticipantInfo">
                          <p>{member.name}</p>
                          <span>{member.isLocal ? (isModerator ? 'Moderator' : 'You') : 'Student'}</span>
                        </div>
                        <div className="videoRoomParticipantStatus">
                          <span className={member.audioState === 'playable' ? 'on' : 'off'}>🎤</span>
                          <span className={member.videoState === 'playable' ? 'on' : 'off'}>🎥</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {activePanel === 'chat' && (
              <div className="videoRoomPanel">
                <div className="videoRoomChat">
                  {chatEnabled && chatMessages.length === 0 && (
                    <div className="videoRoomEmpty">No messages yet.</div>
                  )}
                  {!chatEnabled && (
                    <div className="videoRoomEmpty">Chat is unavailable for this call.</div>
                  )}
                  {chatEnabled &&
                    chatMessages.map((msg) => {
                      const isMine = msg.senderId === auth.currentUser?.uid
                      const avatar = getUserAvatarById(msg.senderId)
                      return (
                        <div key={msg.id} className={`videoRoomChatItem ${isMine ? 'mine' : ''}`}>
                          {!isMine && (
                            <img className="videoRoomChatAvatar" src={avatar} alt={msg.senderName || 'Student'} />
                          )}
                          <div className={`videoRoomChatBubble ${isMine ? 'mine' : ''}`}>
                            <div className="videoRoomChatHeader">
                              <span className="videoRoomChatName">{msg.senderName || 'Student'}</span>
                              <span className="videoRoomChatTime">
                                {formatTime(msg.timestamp || msg.clientTimestamp)}
                              </span>
                            </div>
                            <div
                              className="videoRoomChatText"
                              dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(msg.text) }}
                            />
                          </div>
                        </div>
                      )
                    })}
                </div>
                <div className="videoRoomChatInput">
                  <input
                    placeholder={chatEnabled ? "Type something..." : "Chat unavailable"}
                    value={chatInput}
                    onChange={(event) => setChatInput(event.target.value)}
                    disabled={!chatEnabled}
                  />
                  <button type="button" onClick={sendChatMessage} disabled={!chatEnabled}>
                    Send
                  </button>
                </div>
              </div>
            )}
          </aside>
        </main>

        <footer className="videoRoomControls">
          <button type="button" className="ghost" onClick={toggleAudio}>
            <FontAwesomeIcon icon={isMuted ? faMicrophoneSlash : faMicrophone} />
          </button>
          <button type="button" className="ghost" onClick={toggleVideo}>
            <FontAwesomeIcon icon={isVideoOff ? faVideoSlash : faVideo} />
          </button>
          <button
            type="button"
            className={isScreenSharing ? 'ghost active' : 'ghost'}
            onClick={toggleScreenShare}
          >
            <FontAwesomeIcon icon={faDisplay} />
          </button>
          <button type="button" className="ghost" onClick={() => setActivePanel('chat')}>
            <FontAwesomeIcon icon={faComments} />
          </button>
          <button type="button" className="ghost" onClick={toggleStageFullscreen}>
            <FontAwesomeIcon icon={isStageFullscreen ? faCompress : faExpand} />
          </button>
          <button type="button" className="ghost">
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
          <button type="button" className="danger" onClick={leaveCall}>
            <FontAwesomeIcon icon={faPhoneSlash} /> Leave Meeting
          </button>
        </footer>
      </div>
    </div>
  )
}

export default VideoRoom
